import _ from 'lodash';
import typeHandlers from './handlers/index.js';
import defaultPlugin from './defaultPlugin.js';

export default function makeImport(files, dependencies = {}, plugins = []) {
  const _modules = {};
  let _plugins = [...plugins, defaultPlugin];
  let _typeHandlers;

  const resolveId = _.memoize(
    (id, parentId) => {
      const originalId = id;
      _.forEach(_plugins, plugin => {
        if (plugin.resolveId) {
          id = plugin.resolveId({ id, originalId, parentId, files }) || id;
        }
      });
      return id;
    },
    (id, parentId) => id + parentId
  );

  const load = (id, parentId) => {
    _.forEach(_plugins, plugin => {
      if (plugin.load) {
        try {
          _modules[id] = { id, exports: {} };
          _modules[id].exports = plugin.load({
            id,
            parentId,
            files,
            modules: _modules,
            typeHandlers: _typeHandlers
          });
          if (_modules[id].exports) return false;
        } catch (err) {
          // eslint-disable-next-line no-debugger
          debugger;
          err.message = `Unable to process module ${id}. ${err.message}`;
          throw err;
        }
      }
    });
  };

  const enhance = id => {
    _.forEach(_plugins, plugin => {
      if (plugin.enhance) {
        plugin.enhance(_modules[id]);
      }
    });
  };

  const getExports = (id, parentId) => {
    id = resolveId(id, parentId);

    if (!_.get(_modules[id], 'exports')) {
      load(id, parentId);
      enhance(id, parentId);
    }

    return _.get(_modules[id], 'exports');
  };

  const getDependency = id => {
    // the requested dependency perfectly matches a known dependency
    let dependency = dependencies[id];
    if (dependency) {
      return dependency.isAsync ? dependency() : dependency;
    }

    // the requested dependency might have a moduleName
    const parts = id.split('/');
    const packageName = _.take(parts, parts.length - 1);
    const moduleName = parts[parts.length - 1];

    dependency = dependencies[packageName];
    if (!dependency) return;

    if (dependency.isAsync) {
      return dependency().then(lib => ({ default: lib[moduleName] }));
    }

    if (!moduleName) return dependency;
    return dependency[moduleName];
  };

  const _import = (id, parentId) => {
    const _exports =
      getDependency(id) || getExports(id, parentId) || getExports(id + '/index', parentId);
    if (!_exports) {
      throw new Error(`Unknown module '${id}'.`);
    }
    return _exports;
  };

  _typeHandlers = _.mapValues(typeHandlers, v => v(_import));

  // eslint-disable-next-line no-shadow
  _import.registerPlugins = plugins => {
    _plugins = [...plugins, defaultPlugin];
  };

  return _import;
}
